import { PageHeaderWrapper, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert } from "@mui/material";
import Channel from "@sendbird/uikit-react/Channel";
import sendbirdSelectors from "@sendbird/uikit-react/sendbirdSelectors";
import { type CoreMessageType, type SendableMessageType } from "@sendbird/uikit-react/types/utils";
// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line import/no-named-as-default
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { ChatPageWrapper } from "@src/appV2/Chat/ChatPageWrapper";
import { CHAT_MESSAGE_SPACING } from "@src/appV2/Chat/constants";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { ChatSeparator } from "@src/appV2/redesign/Chat/ChatSeparator";
import { MessageContentWithLongPress } from "@src/appV2/redesign/Chat/components/MessageContentWithLongPress";
import { CustomMessageInput } from "@src/appV2/redesign/Chat/CustomMessageInput";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format } from "date-fns";
import { type ReactElement } from "react";
import { useParams } from "react-router-dom";

import { useGetChatChannels } from "../../Chat/ChatList/hooks/useGetChatChannels";
import { CustomMessageTypes } from "../../Chat/type";
import { useLogEffect } from "../../lib/analytics/useLogEffect";
import { useSearchParams } from "../../lib/utils/useSearchParams";
import { useDefinedWorker } from "../../Worker/useDefinedWorker";
import { useCredentialRequirementConfiguration } from "../Documents/useCredentialRequirementConfiguration";
import { transformPlacementData, useGetPlacement } from "../Placements/api/useGetPlacement";
import {
  hasAppliedToPlacement,
  hasBookedInterview,
  hasCancelledInterview,
  interviewRequired,
} from "../Placements/utils";
import { ChatSkeleton } from "./components/ChatSkeleton";
import { PlacementChatHeader } from "./components/PlacementChatHeader";
import { PlacementChatInterviewScheduledMessage } from "./components/PlacementChatInterviewScheduledMessage";
import { PlacementChatMessage } from "./components/PlacementChatMessage";
import { PlacementChatRejectionMessage } from "./components/PlacementChatRejectionMessage";
import { PlacementSentByClipboardChatMessage } from "./components/PlacementSentByClipboardChatMessage";

function getSenderName(message: SendableMessageType): string {
  const { sender } = message;
  return sender?.friendName ?? sender?.nickname ?? sender?.userId ?? "";
}

function renderDateSeparator({ message }: { message: { createdAt: number } }) {
  const createdAt = new Date(message.createdAt);
  const date = format(createdAt, "MMM d, h:mm");
  const meridiem = format(createdAt, "a").toLowerCase();

  return (
    <ChatSeparator sx={{ marginY: CHAT_MESSAGE_SPACING }}>
      {date} {meridiem}
    </ChatSeparator>
  );
}

function isSendableMessage(message: CoreMessageType): message is SendableMessageType {
  return message.messageType === "user" || message.messageType === "file";
}

function renderMessageHeader({ message }: { message: CoreMessageType }) {
  if (!isSendableMessage(message)) {
    return null;
  }

  return (
    <Text variant="caption" color="text.secondary" sx={{ paddingY: 2, paddingX: 0 }}>
      {getSenderName(message)}
    </Text>
  );
}

export function PlacementChatPage() {
  const { placementId, workplaceId } = useParams<{
    placementId: string;
    workplaceId: string;
  }>();
  const { source } = useSearchParams();
  const worker = useDefinedWorker();

  const {
    data: placement,
    isLoading: isPlacementLoading,
    isSuccess: isPlacementSuccess,
  } = useGetPlacement(
    { placementId, workerId: worker.userId },
    { enabled: isDefined(placementId) && isDefined(worker.userId) }
  );

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  const placementDetails = isPlacementSuccess
    ? transformPlacementData(placement, credentialRequirementConfiguration)
    : undefined;

  const sendBirdState = useSendbirdStateContext();
  const sdk = sendbirdSelectors.getSdk(sendBirdState);
  const sendBirdGroupChannelSdk = sdk?.groupChannel;
  const {
    isLoading: isLoadingChatChannels,
    data: chatChannelsData,
    error: chatChannelsError,
  } = useGetChatChannels({ enabled: isDefined(sendBirdGroupChannelSdk) && isDefined(placementId) });
  const currentChannel = chatChannelsData?.channels?.find(
    (channel) => channel.url === `${workplaceId}_${worker.userId}_${placementId}`
  );

  useLogEffect(
    APP_V2_APP_EVENTS.PLACEMENT_CHAT_PAGE_VIEWED,
    {
      placementId,
      workplaceId,
      workerId: worker.userId,
      source,
      channelUrl: currentChannel?.url,
      hasApplied: hasAppliedToPlacement(placementDetails),
      hasBookedInterview: hasBookedInterview(placementDetails),
      hasCancelledInterview: hasCancelledInterview(placementDetails),
      interviewRequired: interviewRequired(placementDetails),
      currentPlacementStatus: placementDetails?.applications?.[0]?.attributes?.status ?? "unknown",
    },
    { enabled: isDefined(currentChannel) && isDefined(worker) && isDefined(placementDetails) }
  );

  const showChatLoader =
    (isLoadingChatChannels || isPlacementLoading) && !isDefined(sendBirdGroupChannelSdk);
  const showChatError = isDefined(sendBirdGroupChannelSdk) && chatChannelsError;
  const showChatNotFound =
    isDefined(sendBirdGroupChannelSdk) && !isLoadingChatChannels && !isDefined(currentChannel);

  return (
    <ChatPageWrapper>
      {showChatLoader && <ChatSkeleton />}
      {showChatError && (
        <Alert severity="error">{chatChannelsError.message ?? "Error loading chat channels"}</Alert>
      )}
      {showChatNotFound && <Alert severity="error">Could not find chat channel</Alert>}
      {isDefined(currentChannel) && (
        <Channel
          channelUrl={currentChannel.url}
          isReactionEnabled={false}
          renderChannelHeader={() => (
            <PageHeaderWrapper variant="tertiary" sx={{ boxShadow: (theme) => theme.shadows[2] }}>
              <PlacementChatHeader channel={currentChannel} placement={placementDetails} />
            </PageHeaderWrapper>
          )}
          renderCustomSeparator={renderDateSeparator}
          renderMessageInput={() => <CustomMessageInput />}
          renderMessageContent={(contentProps) => (
            <MessageContentWithLongPress
              message={contentProps.message}
              channel={contentProps.channel!}
              userId={contentProps.userId}
              chainTop={contentProps.chainTop}
              chainBottom={contentProps.chainBottom}
              renderMessageHeader={renderMessageHeader}
            />
          )}
          renderMessage={({ message }) => {
            if (message.customType === CustomMessageTypes.PLACEMENT_APPLICATION) {
              return PlacementChatMessage({
                message,
                channel: currentChannel,
              });
            }

            if (
              message.customType ===
                CustomMessageTypes.PLACEMENT_APPLICATION_AUTO_INTERVIEW_INVITE ||
              message.customType === CustomMessageTypes.PLACEMENT_APPLICATION_SUGGEST_INTERVIEW
            ) {
              // do not render this message in the mobile app
              return null as unknown as ReactElement;
            }

            if (
              message.customType ===
              CustomMessageTypes.PLACEMENT_APPLICATION_CHAT_INTERVIEW_SCHEDULED
            ) {
              return PlacementChatInterviewScheduledMessage({
                message,
                channel: currentChannel,
              });
            }

            if (
              message.customType === CustomMessageTypes.PLACEMENT_APPLICATION_REJECTION_BY_WORKPLACE
            ) {
              return PlacementChatRejectionMessage({
                message,
                channel: currentChannel,
              });
            }

            if (message.customType === CustomMessageTypes.INTERVIEW_WORKER_CANCELLATION) {
              return PlacementSentByClipboardChatMessage({
                message,
                channel: currentChannel,
                customType: message.customType,
              });
            }

            // this causes the message to render by default
            return null as unknown as ReactElement;
          }}
        />
      )}
    </ChatPageWrapper>
  );
}
